import { Box, Card, Link, Typography, ResponsiveLayout } from "@bca/ui-library";
import React, { useState } from "react";
import styled from "styled-components";

import { formatMoney } from "./buyingOptions/formatMoney";
import OptionalEquipmentAccordion from "./OptionalEquipmentAccordion";
import StarRating from "./StarRating";
import StarsRatingGuideCard from "./StarsRatingGuide";

const StyledBox = styled(Box)`
  border-bottom: 1px dashed #f0f0f0;
`;

const HPISpecCheckTable: React.FunctionComponent<{
  starRating?: number | null;
  basePrice?: number | null;
  optionsPrice?: number | null;
  totalPrice?: number | null;
  specCheckOptions?:
    | null
    | {
        shortDescription?: string | null;
        longDescription?: string | null;
        basicPrice?: number | null;
        optionPrice?: number | null;
        optionKind?: string | null;
      }[];
}> = ({
  starRating,
  basePrice,
  optionsPrice,
  totalPrice,
  specCheckOptions,
}) => {
  const [showStarsRatingGuide, setShowStarsRatingGuide] = useState(false);

  const handleStarsRatingModalLink = () => {
    setShowStarsRatingGuide(true);
  };

  if (!Boolean(starRating) || !Boolean(optionsPrice)) return null;

  return (
    <Box id="hpi-spec-check-table" backgroundColor="#fafafa">
      <ResponsiveLayout pt={9}>
        <Card marginX={0} marginY={0} width="100%" height="100%">
          <StyledBox>
            <Box display="flex" justifyContent="space-between">
              <Typography
                variant="h5"
                marginTop={{ mobile: "0px", tablet: "10px", desktop: "10px" }}
                marginBottom="15px"
              >
                HPI Spec Check*
              </Typography>
              <Box display="flex">
                <Typography
                  variant="h6"
                  marginTop={{ mobile: "4px", tablet: "14px", desktop: "14px" }}
                  marginBottom="15px"
                  display={{
                    mobile: "none",
                    tablet: "block",
                    desktop: "block",
                  }}
                >
                  Options Rating
                </Typography>
                <Box
                  alignContent="center"
                  margin="11px 0 15px 12px"
                  marginTop={{ mobile: "0px", tablet: "11px", desktop: "11px" }}
                >
                  <StarRating
                    rating={starRating.toFixed(1).toString()}
                    variant="large"
                  />
                </Box>
              </Box>
            </Box>
            <Typography
              variant="body"
              color="grey-blue"
              css={`
                max-width: 700px;
              `}
            >
              Details of factory fitted optional equipment and the prices they
              were originally valued at when new. The Options Rating helps to
              make this measurable against other vehicles.{" "}
              <Link
                color="primary-light-blue"
                underline="always"
                analytics={{
                  eventLabel: "hpi-check-ratings",
                  eventAction: "popup",
                }}
                onClick={handleStarsRatingModalLink}
              >
                How is this calculated?
              </Link>
              {showStarsRatingGuide && (
                <StarsRatingGuideCard
                  isOpen={showStarsRatingGuide}
                  setShowStarsRatingGuide={setShowStarsRatingGuide}
                />
              )}
            </Typography>
            <Box display="flex" justifyContent="space-between">
              <Typography
                variant="h6"
                marginTop="10px"
                marginBottom="10px"
                css={`
                  @media screen and (max-width: 414px) {
                    max-width: 160px;
                  }
                `}
              >
                Vehicle base price when new
              </Typography>
              <Box
                alignContent="center"
                margin="auto 0"
                display="flex"
                marginTop="10px"
                flexDirection={{
                  mobile: "column",
                  tablet: "row",
                  desktop: "row",
                }}
                alignItems="center"
              >
                {Boolean(basePrice) ? (
                  <>
                    <Typography variant="h6" margin="0">
                      {formatMoney(basePrice)}{" "}
                    </Typography>
                    <Typography
                      variant="body"
                      color="grey-blue"
                      margin="0"
                      marginLeft="3px"
                    >
                      inc. VAT
                    </Typography>
                  </>
                ) : (
                  <Typography variant="h6" margin="0">
                    -
                  </Typography>
                )}
              </Box>
            </Box>
            <OptionalEquipmentAccordion
              hpiOptions={specCheckOptions}
              optionsPrice={optionsPrice}
            />
            <Box display="flex" justifyContent="space-between">
              <Typography
                variant="h6"
                marginTop="10px"
                marginBottom="10px"
                css={`
                  @media screen and (max-width: 414px) {
                    max-width: 110px;
                  }
                `}
              >
                Total price when new
              </Typography>
              <Box
                alignContent="center"
                margin="auto 0"
                display="flex"
                marginTop="10px"
                marginBottom="10px"
                flexDirection={{
                  mobile: "column",
                  tablet: "row",
                  desktop: "row",
                }}
                alignItems="center"
              >
                {Boolean(totalPrice) ? (
                  <>
                    <Typography variant="h5" margin="0">
                      {formatMoney(totalPrice)}{" "}
                    </Typography>
                    <Typography
                      variant="body"
                      color="grey-blue"
                      margin="0"
                      marginLeft="3px"
                      marginTop={{
                        mobile: "0",
                        tablet: "1",
                        desktop: "1",
                      }}
                    >
                      inc. VAT
                    </Typography>
                  </>
                ) : (
                  <Typography variant="h5" margin="0">
                    -
                  </Typography>
                )}
              </Box>
            </Box>
          </StyledBox>
          <Typography variant="body" color="grey-blue">
            * HPI Spec Check data is provided by CAP HPI, with all rights
            reserved to CAP HPI. BCA shall not be liable for any errors or
            omissions in the HPI Spec Check data or for any liability or loss
            suffered by any person as a result of that person’s use of such
            data. BCA does not warrant that any original specification equipment
            listed in the HPI Spec Check for any Vehicle still remains fitted to
            that Vehicle. Please see our{" "}
            <Link
              color="primary-light-blue"
              underline="always"
              href="https://www.bca.co.uk/legal/terms-conditions/conditions-of-entry-and-sale"
              target="_blank"
              analytics={{
                eventLabel: "terms-conditions",
                eventAction: "navigate",
              }}
            >
              terms and conditions.
            </Link>
          </Typography>
        </Card>
      </ResponsiveLayout>
    </Box>
  );
};

export default HPISpecCheckTable;
